import React from 'react'
import cardPositionControlsUtils from './cardPositionControls.utils'
import { IWidget } from '@/pages/Dashboards/dashboards.types.ts'
import { CardMoveType } from '@/pages/Dashboards/services/autoalign/autoalign.types.ts'
import css from '../widgetCard.module.css'
import cn from '@/utils/cn.ts'

interface Props<T extends IWidget> {
  cardRef: React.RefObject<HTMLDivElement>
  component: T
  onMove: (position: T, type: CardMoveType) => void
  onMoveFinish: () => void
  onResize: () => void
  viewport: number
}

/**
 * Элементы управления для перетаскивания и изменения размера элементов экрана
 */
const CardPositionControls = <T extends IWidget>(props: Props<T>) => {
  const c = props.component
  const conf = c.configuration
  const vw = props.viewport

  const handleDrag = (onMove, onFinish?) => {
    function cleanup(e) {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', cleanup)

      if (onFinish) onFinish(e)
      props.onMoveFinish()
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', cleanup)
  }

  // при нажатии на верхнюю часть панели перейти в режим перетаскивания
  const handleMove = (event) => {
    const card = props.cardRef.current
    const [x, y] = cardPositionControlsUtils.parseTranslate(card.style.transform)
    const width = parseFloat(card.style.width)
    const x0 = event.pageX
    const y0 = event.pageY

    handleDrag((e) => {
      let x1 = x + e.pageX - x0
      let y1 = y + e.pageY - y0

      if (x1 < 0) x1 = 0
      if (y1 < 0) y1 = 0
      if (x1 > vw - width) x1 = vw - width

      card.style.transform = `translate(${x1}px, ${y1}px)`
      props.onMove({ ...c, configuration: { ...conf, topLeftX: x1 / vw, topLeftY: y1 / vw } }, 'move')
    })
  }

  // при нажатии на углы панели перейти в режим изменения размеров
  const handleResize = (event) => {
    const card = props.cardRef.current
    const factor = cardPositionControlsUtils.factors[event.currentTarget.dataset.id]

    const minHeight = cardPositionControlsUtils.minHeights[c.type] || 50
    const minWidth = cardPositionControlsUtils.minWidths[c.type] || 50

    // начальные размеры в момент нажатия
    const [x0, y0] = cardPositionControlsUtils.parseTranslate(card.style.transform)
    const w0 = parseFloat(card.style.width)
    const h0 = parseFloat(card.style.height)
    const startX = event.pageX
    const startY = event.pageY

    // при движении мыши рассчитать новые размеры
    const calcPosition = (e) => {
      let dx = e.pageX - startX
      let dy = e.pageY - startY

      // запрещаем делать элемент меньше указанных минимальных размеров
      if (w0 + factor.w * dx < minWidth) dx = -(w0 - minWidth) * factor.w
      if (h0 + factor.h * dy < minHeight) dy = -(h0 - minHeight) * factor.h

      // запрет на выход элемента за границы экрана
      if (x0 + factor.x * dx < 0) dx = -x0 * factor.x
      if (y0 + factor.y * dy < 0) dy = -y0 * factor.y
      if (x0 + factor.x * dx + w0 + factor.w * dx > vw) dx = vw - x0 - w0

      const x = x0 + factor.x * dx
      const y = y0 + factor.y * dy
      const w = w0 + factor.w * dx
      const h = h0 + factor.h * dy

      props.onMove(
        {
          ...c,
          configuration: { ...c.configuration, topLeftX: x / vw, topLeftY: y / vw, width: w / vw, height: h / vw },
        },
        'resize'
      )
      return { x, y, w, h }
    }

    const onMove = (e) => {
      const { x, y, w, h } = calcPosition(e)
      card.style.transform = `translate(${x}px, ${y}px) scale(${w / w0}, ${h / h0})`
    }

    const onFinish = (e) => {
      const { x, y, w, h } = calcPosition(e)

      card.style.transform = `translate(${x}px, ${y}px)`
      card.style.width = w + 'px'
      card.style.height = h + 'px'
      props.onResize()
    }

    handleDrag(onMove, onFinish)
  }

  // задание позиции элемента
  React.useEffect(() => {
    const card = props.cardRef.current

    card.style.width = Math.round(vw * +conf.width) + 'px'
    card.style.height = Math.round(vw * +conf.height) + 'px'
    card.style.transform = `translate(${Math.round(vw * conf.topLeftX)}px, ${Math.round(vw * conf.topLeftY)}px)`
    props.onResize()
  }, [vw, conf.topLeftX, conf.topLeftY, conf.width, conf.height])

  return (
    <React.Fragment>
      <div className={cn(css.cardControl, css.isTop)} onMouseDown={handleMove} />
      <div data-id="rb" className={cn(css.cardControl, css.isRb)} onMouseDown={handleResize} />
      <div data-id="lb" className={cn(css.cardControl, css.isLb)} onMouseDown={handleResize} />
    </React.Fragment>
  )
}

export default CardPositionControls
